/**
 * SkilsmisseVejen — Delte utilities, hooks og konstanter
 * Importér herfra i stedet for fra App.js
 *
 * Eksporterer:
 *   Konstanter: ANALYSERBARE_FILER, MAX_FIL_STOERRELSE, MAX_TOTAL_STOERRELSE, MAX_ANTAL_FILER
 *   Hooks:      useToast, useAutoSave, useEscapeClose, useResponsive
 *   Funktioner: toast, fortryd_toast, pollAiOpgave, beregn_responsive, post_crash_log
 *   Objekter:   autoSaveManager
 */
import { useState, useEffect } from 'react';
import { api } from './api';

// ─────────────────────────────────────────────────────────
//  FIL-KONSTANTER
// ─────────────────────────────────────────────────────────
export const ANALYSERBARE_FILER = /\.(pdf|docx|doc|eml|txt|msg)$/i;
export const MAX_FIL_STOERRELSE = 45 * 1024 * 1024;
export const MAX_TOTAL_STOERRELSE = 200 * 1024 * 1024;
export const MAX_ANTAL_FILER = 500;

// ─────────────────────────────────────────────────────────
//  AI-OPGAVE POLLING
// ─────────────────────────────────────────────────────────
export function pollAiOpgave(opgave_id, onResult, onError, endpoint) {
  const ep = endpoint || 'ai-sag/opgave/';
  const max = 100;
  let n = 0;
  const timer = setInterval(async () => {
    n++;
    if (n > max) { clearInterval(timer); onError && onError(new Error('AI-analyse tog for lang tid')); return; }
    try {
      const r = await api(ep + opgave_id);
      if (r === null || r?.fejl) { clearInterval(timer); onError && onError(new Error(r?.fejl || 'AI-opgave ikke fundet')); return; }
      if (r?.status === 'FULDFOERT' && r.resultat) { clearInterval(timer); onResult(r.resultat); }
      else if (r?.status === 'FEJLET') { clearInterval(timer); onError && onError(new Error(r.fejl_besked || 'AI-opgave fejlede')); }
    } catch (_) { /* silent poll retry */ }
  }, 3000);
  return timer;
}

// ─────────────────────────────────────────────────────────
//  TOAST-SYSTEM
//  Typer: 'success' (grøn), 'fejl' (rød), 'info' (blå), 'advarsel' (gul)
//  Toasts kan have en action-knap (fx "Fortryd") der trigger callback
// ─────────────────────────────────────────────────────────
let _toast_listeners = [];

export function useToast() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const handler = (besked, type, opts) => {
      const id = Date.now() + Math.random();
      const varighed = opts?.varighed || (opts?.action ? 6000 : 4000);
      setToasts(p => [...p, { id, besked, type: type || 'info', action: opts?.action, onAction: opts?.onAction }]);
      setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), varighed);
    };
    const fjern_handler = (id) => setToasts(p => p.filter(t => t.id !== id));
    _toast_listeners.push(handler);
    window.__sv_toast = handler;
    window.__sv_toast_fjern = fjern_handler;
    return () => { _toast_listeners = _toast_listeners.filter(l => l !== handler); };
  }, []);
  return toasts;
}

export function toast(besked, type, opts) {
  if (window.__sv_toast) window.__sv_toast(besked, type, opts);
}

// ─────────────────────────────────────────────────────────
//  FORTRYD-TOAST
//  Viser 6s toast med "Fortryd"-knap. Kalder onUndo() hvis klikket.
//  Returnerer Promise<bool> — true = handling udføres, false = fortrydt.
// ─────────────────────────────────────────────────────────
export function fortryd_toast(besked, onUndo) {
  return new Promise(resolve => {
    let undone = false;
    toast(besked, 'advarsel', {
      varighed: 6000,
      action: 'Fortryd',
      onAction: async () => {
        undone = true;
        if (onUndo) await onUndo();
        toast('Handlingen er fortrydt', 'success');
        resolve(false);
      }
    });
    setTimeout(() => { if (!undone) resolve(true); }, 6000);
  });
}

// ─────────────────────────────────────────────────────────
//  AUTO-SAVE DRAFT (localStorage)
//  Brug: useAutoSave('klient-wizard-' + id, formState)
//  Gendannelse: autoSaveManager.hent('nøgle')
// ─────────────────────────────────────────────────────────
export const autoSaveManager = {
  gem(nøgle, data) {
    try { localStorage.setItem('sv_draft_' + nøgle, JSON.stringify({ data, tid: Date.now() })); } catch (_) {}
  },
  hent(nøgle, max_ælde_ms = 24 * 60 * 60 * 1000) {
    try {
      const raw = localStorage.getItem('sv_draft_' + nøgle);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - (parsed.tid || 0) > max_ælde_ms) return null;
      return parsed.data;
    } catch (_) { return null; }
  },
  slet(nøgle) {
    try { localStorage.removeItem('sv_draft_' + nøgle); } catch (_) {}
  },
  listDrafts() {
    const out = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('sv_draft_')) {
          const raw = localStorage.getItem(k);
          try { out.push({ nøgle: k.replace('sv_draft_', ''), ...JSON.parse(raw) }); } catch (_) {}
        }
      }
    } catch (_) {}
    return out;
  }
};

export function useAutoSave(nøgle, data, options = {}) {
  const { interval_ms = 30000, aktiv = true } = options;
  useEffect(() => {
    if (!aktiv || !nøgle) return;
    if (data === null || data === undefined) return;
    const t = setInterval(() => autoSaveManager.gem(nøgle, data), interval_ms);
    return () => clearInterval(t);
  }, [nøgle, data, interval_ms, aktiv]);
}

// ─────────────────────────────────────────────────────────
//  ESCAPE-CLOSE (modal-stack)
//  Stack-baseret — kun øverste modal lukkes per Escape-tryk
// ─────────────────────────────────────────────────────────
const _escape_stack = [];

export function useEscapeClose(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const entry = { onClose };
    _escape_stack.push(entry);
    const handler = e => {
      if (e.key === 'Escape') {
        const top = _escape_stack[_escape_stack.length - 1];
        if (top === entry) {
          e.preventDefault();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      const idx = _escape_stack.lastIndexOf(entry);
      if (idx !== -1) _escape_stack.splice(idx, 1);
    };
  }, [isOpen, onClose]);
}

// ─────────────────────────────────────────────────────────
//  RESPONSIVE HOOK
//  Breakpoints: mobil (<640), tablet (640-1023), desktop (≥1024)
// ─────────────────────────────────────────────────────────
export function beregn_responsive() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  const landscape = w > h;
  const mobil = w < 640;
  const mobil_landscape = landscape && h <= 500 && w <= 1000;
  const tablet = w >= 640 && w < 1024;
  const desktop = w >= 1024;
  return {
    bredde: w, hoejde: h, landscape,
    mobil_portrait: mobil && !landscape,
    mobil_landscape,
    er_mobil: mobil,
    tablet_portrait: tablet && !landscape,
    tablet_landscape: tablet && landscape,
    er_tablet: tablet,
    desktop,
    lav_hoejde: h <= 500,
    touch: typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0),
  };
}

export function useResponsive() {
  const [state, setState] = useState(() => beregn_responsive());
  useEffect(() => {
    const handler = () => setState(beregn_responsive());
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);
  return state;
}

// ─────────────────────────────────────────────────────────
//  CRASH-LOGGER
//  Sender fejlinfo til /api/fejl-log (fail-silent)
// ─────────────────────────────────────────────────────────
export function post_crash_log(fejl, info, komponent) {
  try {
    const body = {
      komponent: komponent || 'ukendt',
      fejl_type: fejl?.name || 'Error',
      fejl_besked: String(fejl?.message || fejl || '').slice(0, 2000),
      stack: String(fejl?.stack || '').slice(0, 10000),
      url: window.location.href,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
    fetch('/api/fejl-log', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────
//  GLOBAL RESPONSIVE CSS — injiceres én gang via <style>
// ─────────────────────────────────────────────────────────
const RESPONSIVE_CSS = `
/* Base */
/* v1.6.80: RODÅRSAG FUNDET via CDP computed-style-test (Playwright/Chromium):
   "pan-x pan-y pinch-zoom" normaliseres til "manipulation" af Chromium,
   og "manipulation" BLOKERER pinch-gesture på Android/iOS (det er kun
   tap + double-tap-zoom, IKKE pinch). Alle tre foregående forsøg satte
   netop denne kombination → alle tre fejlede af PRÆCIST samme årsag.
   Fix: touch-action: auto — tillader pan + pinch + scroll uden blokering.
   Horisontal overflow forhindres af overflow-x:hidden/clip — touch-action
   behøver ikke at blokere pan-x. */
html, body { margin:0; padding:0; max-width: 100vw; width: 100%; }
body  { overflow-x: hidden; touch-action: auto; }
#root { overflow-x: clip;   touch-action: auto; width: 100%; }
body { -webkit-text-size-adjust: 100%; font-size: 15px; color: #e2e8f0; }
*, *::before, *::after { box-sizing: border-box; }
img, video, iframe, svg, canvas { max-width: 100%; height: auto; }
textarea, input, select { max-width: 100%; }
button, [role="button"] { min-height: 44px; }
input[type="checkbox"], input[type="radio"] { min-height: auto; min-width: auto; }
button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, [role="button"]:focus-visible, [tabindex]:focus-visible {
  outline: 3px solid #3fd4c7; outline-offset: 2px; border-radius: 4px;
}
.sv-skip-link {
  position: absolute; left: -9999px; top: 8px;
  background: #2322be; color: #fff; padding: 10px 16px; border-radius: 6px;
  z-index: 100002; font-weight: 600; font-family: 'Inter', system-ui, sans-serif; text-decoration: none;
}
.sv-skip-link:focus { left: 16px; outline: 3px solid #3fd4c7; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
.sv-main, .sv-topbar, .sv-admin-content { min-width: 0; word-wrap: break-word; overflow-wrap: break-word; }
@media (pointer: coarse) {
  button, [role="button"], input, select, textarea, a { min-height: 44px; }
  input[type="checkbox"], input[type="radio"] { min-height: auto; }
}
@media (pointer: coarse), (max-width: 767px) {
  .sv-os-sidebar { display: none !important; }
  .sv-os-bottomnav { display: flex !important; }
  .sv-os-main { margin-left: 0 !important; padding-left: 16px !important; padding-right: 16px !important; padding-top: 56px !important; padding-bottom: 80px !important; }
}
@media (pointer: fine) and (min-width: 768px) { .sv-os-bottomnav { display: none !important; } }
@media (max-width: 767px) {
  /* v1.6.80: touch-action: auto — pan-x pan-y pinch-zoom normaliseres af
     Chromium til "manipulation" der blokerer pinch. Brug auto i stedet. */
  html, body { max-width: 100vw !important; overflow-x: hidden !important; }
  #root { max-width: 100vw !important; overflow-x: clip !important; touch-action: auto !important; }
  nav, section, main, article, div { min-width: 0; }
  .sv-sidebar-desktop { display: none !important; }
  .sv-topbar { left: 0 !important; width: 100% !important; height: 56px !important; min-height: 56px !important; max-height: 56px !important; padding: 0 14px !important; flex-wrap: nowrap !important; gap: 8px !important; overflow: hidden; }
  .sv-topbar > div { min-width: 0; flex-shrink: 1; }
  .sv-main { margin-left: 0 !important; padding-bottom: 72px !important; padding-top: 56px !important; width: 100% !important; }
  .sv-topbar-live { display: none !important; }
  .sv-topbar-logout { display: flex !important; flex-shrink: 0; }
  .sv-topbar-theme { display: flex !important; flex-shrink: 0; }
  .sv-topbar-subtitle { display: none !important; }
  .sv-topbar-greeting { font-size: 16px !important; font-weight: 600 !important; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sv-topbar-page-title { font-size: 15px !important; }
  button, [role=button] { min-height: 44px !important; }
  .sv-topbar button { padding: 7px 12px !important; font-size: 12px !important; white-space: nowrap; min-height: 36px !important; }
  .sv-topbar-skjul-mobil { display: none !important; }
  .sv-tabs-sticky { position: sticky !important; top: 0 !important; z-index: 40 !important; background: rgba(8,12,26,0.95) !important; backdrop-filter: blur(8px) !important; width: 100% !important; box-sizing: border-box !important; }
  .sv-kpi-grid { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
  .sv-kpi-grid > div { padding: 14px 16px !important; }
  .sv-stat-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
  .sv-sag-grid { grid-template-columns: 1fr !important; }
  .sv-sag-header { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
  .sv-sag-header-sep { display: none !important; }
  .sv-admin-panel { flex-direction: column !important; min-height: auto !important; }
  .sv-admin-sidebar { width: 100% !important; min-height: auto !important; overflow-x: auto !important; overflow-y: hidden !important; display: flex !important; flex-direction: row !important; padding: 8px !important; border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.06); white-space: nowrap; }
  .sv-admin-sidebar > div { flex-shrink: 0 !important; border-left: none !important; border-bottom: 3px solid transparent !important; padding: 10px 14px !important; }
  .sv-admin-content { padding: 16px !important; max-width: 100% !important; width: 100% !important; }
  .sv-modal { inset: 0 !important; border-radius: 0 !important; max-width: 100vw !important; max-height: 100vh !important; width: 100% !important; height: 100% !important; overflow-y: auto; }
  .sv-form-grid { grid-template-columns: 1fr !important; }
  .sv-bottom-nav { display: flex !important; }
  .sv-skjul-mobil { display: none !important; }
  h1 { font-size: 20px !important; }
  table { min-width: 480px; }
  .sv-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; max-width: 100vw; }
  input, textarea, select { font-size: 16px !important; }
  .sv-main > div { max-width: 100vw; }
  [role="dialog"], div[style*="position: fixed"][style*="width:420"],
  div[style*="position:fixed"][style*="width:420"] { max-width: calc(100vw - 24px) !important; }
  .sv-liste-row { padding: 10px 12px !important; gap: 8px !important; align-items: flex-start !important; }
  .sv-liste-badge-row { flex-wrap: wrap !important; gap: 4px !important; margin-bottom: 4px !important; }
  .sv-liste-status-badge { font-size: 10px !important; padding: 1px 5px !important; }
  .sv-liste-deadline { flex-shrink: 1 !important; min-width: 70px !important; max-width: 100px !important; overflow: hidden; }
  .sv-liste-deadline > div { padding: 4px 7px !important; }
  .sv-liste-deadline > div > div:first-child { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 11px !important; line-height: 1.3; }
  .sv-liste-deadline > div > div:last-child { display: none !important; }
  .sv-liste-deadline > div[style*="dashed"] { padding: 4px 6px !important; font-size: 11px !important; white-space: nowrap; }
  .sv-liste-filterbar { flex-wrap: wrap !important; gap: 6px !important; padding: 10px 12px !important; }
  .sv-liste-filterbar-right { width: 100% !important; justify-content: flex-end !important; }
  .sv-liste-filterbar-right select { font-size: 12px !important; padding: 4px 8px !important; max-width: 130px; }
  .sv-deadline-moeder-grid { grid-template-columns: 1fr !important; }
  .sv-widget-dl-box { flex-shrink: 1 !important; min-width: 60px !important; max-width: 88px !important; overflow: hidden; }
  .sv-widget-dl-box .sv-widget-dl-sub { display: none !important; }
  .sv-infoskaerm-grid { grid-template-columns: 1fr !important; }
  .sv-infoskaerm-kpi-bar { grid-template-columns: repeat(3, 1fr) !important; }
  .sv-liste-row { flex-wrap: wrap !important; }
  .sv-ai-hjaelp-panel { width: 100% !important; height: calc(100vh - 72px) !important; border-radius: 0 !important; }
  .sv-scheduler-table { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
  .sv-scheduler-table > div { min-width: 600px !important; }
  .sv-hjaelp-layout { flex-direction: column !important; }
  .sv-hjaelp-layout > div:first-child { width: 100% !important; }
  .sv-os-statusbar { overflow-x: auto !important; white-space: nowrap !important; scrollbar-width: none !important; }
  .sv-os-statusbar::-webkit-scrollbar { display: none !important; }
  .sv-soeg-instruktion { flex-wrap: wrap !important; font-size: 11px !important; }
  .sv-moede-dialog { padding-bottom: 80px !important; }
  .sv-haendelse-titel { white-space: normal !important; display: -webkit-box !important; -webkit-line-clamp: 2 !important; -webkit-box-orient: vertical !important; overflow: hidden !important; }
  .sv-os-chat-input { padding-bottom: 80px !important; }
  .sv-chief-layout { flex-direction: column !important; }
  .sv-chief-layout > div:first-child { width: 100% !important; max-height: 180px !important; overflow-x: auto !important; }
  .sv-infoskaerm-kpi { grid-template-columns: repeat(2, 1fr) !important; }
  .sv-okonomi-kpi { grid-template-columns: repeat(2, 1fr) !important; }
  .sv-website-studio { flex-direction: column !important; }
  .sv-website-studio > div { width: 100% !important; min-width: 0 !important; }
}
@media (max-height: 500px) and (orientation: landscape) {
  .sv-sidebar-desktop { width: 56px !important; }
  .sv-main { padding-bottom: 0 !important; margin-left: 56px !important; padding-top: 56px !important; }
  .sv-bottom-nav { display: none !important; }
  .sv-topbar { left: 56px !important; padding: 6px 12px !important; min-height: 48px !important; }
  .sv-kpi-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 8px !important; }
  .sv-kpi-grid > div { padding: 10px 12px !important; }
  .sv-form-grid { grid-template-columns: 1fr 1fr !important; }
  .sv-sag-detalje { flex-direction: row !important; }
  .sv-sag-detalje > * { flex: 1; min-width: 0; }
  .sv-modal { max-height: 95vh !important; overflow-y: auto !important; }
  h1 { font-size: 18px !important; margin-bottom: 8px !important; }
  .sv-main > div { max-width: calc(100vw - 56px); }
}
/* FEJL-018-fix: bredde-grænsen her SKAL matche index.css' egen
   ".sv-sidebar-desktop { display:none }"-grænse (max-width:900px, samme
   media-forespørgsel) — ellers opstår et "dødt bånd" i 768-900px hvor
   index.css allerede har skjult den fulde sidebar, men denne blok (som satte
   .sv-topbar/.sv-main op til den SMALLE 56px-sidebar fra blokken ovenfor)
   endnu ikke havde nulstillet layoutet eller vist en erstatning — resultat:
   ingen synlig navigation overhovedet (bekræftet med Playwright 800×360). */
@media (max-width: 900px) and (orientation: landscape) and (max-height: 500px) {
  .sv-sidebar-desktop { display: none !important; }
  .sv-main { margin-left: 0 !important; padding-bottom: 0 !important; }
  .sv-topbar { left: 0 !important; }
  .sv-kpi-grid { grid-template-columns: repeat(3, 1fr) !important; }
}
/* Landskabs-telefoner/-tablets ≤900px bredde: bevar bundnavigation så
   navigation ikke forsvinder. FEJL-018-fix: kravet "(pointer: coarse)" er
   fjernet — det udelukkede bl.a. Playwright-viewport-test uden touch-emulering
   (800×360) fra nogensinde at vise bundnavigationen, selvom den fulde sidebar
   i samme bredde allerede er skjult uanset pointer-type. */
@media (max-width: 900px) and (orientation: landscape) and (max-height: 500px) {
  .sv-bottom-nav { display: flex !important; height: 52px !important; }
  .sv-main { padding-bottom: 60px !important; }
}
@media (min-width: 640px) and (max-width: 1023px) {
  .sv-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .sv-sag-grid { grid-template-columns: 1fr !important; }
  .sv-admin-sidebar { width: 200px !important; }
  .sv-form-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .sv-deadline-moeder-grid { grid-template-columns: 1fr 1fr !important; }
}
@media (min-width: 1024px) and (max-width: 1279px) {
  .sv-kpi-grid { grid-template-columns: repeat(3, 1fr) !important; }
  .sv-sag-grid { grid-template-columns: 1fr 1fr !important; }
}
@keyframes sv-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes infoskaerm-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.75; transform: scale(1.02); } }
.infoskaerm-tooltip { background: rgba(10, 14, 46, 0.9) !important; color: #e2e8f0 !important; border: 1px solid rgba(63, 212, 199, 0.3) !important; border-radius: 6px !important; padding: 4px 8px !important; font-size: 13px !important; font-family: 'Inter', system-ui, sans-serif !important; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important; }
.infoskaerm-tooltip::before { display: none !important; }
.leaflet-popup-content-wrapper { background: #fff !important; border-radius: 10px !important; }
.infoskaerm-postnr-naal { animation: infoskaerm-naal-drop 0.5s ease-out; }
@keyframes infoskaerm-naal-drop { 0% { transform: translateY(-20px) scale(0.7); opacity: 0; } 70% { transform: translateY(2px) scale(1.05); opacity: 1; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
.infoskaerm-pulse-ring path { animation: infoskaerm-pulse-ring 2.5s ease-in-out infinite; }
@keyframes infoskaerm-pulse-ring { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.7; } }
.sv-liste-sagsnr { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
/* Calm Tech Mode — reducér visuel støj og animationer */
body.sv-calm * { animation-duration: 0.01ms !important; transition-duration: 0.15s !important; }
body.sv-calm .sv-holo-card, body.sv-calm .sv-3d-tilt, body.sv-calm .sv-card-pop { transform: none !important; box-shadow: none !important; }
body.sv-calm [style*="background: linear-gradient"], body.sv-calm [style*="background:linear-gradient"] { filter: saturate(0.4) brightness(0.9); }
`;

if (typeof document !== 'undefined' && !document.getElementById('sv-responsive-css')) {
  const style = document.createElement('style');
  style.id = 'sv-responsive-css';
  style.textContent = RESPONSIVE_CSS;
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────
//  CALM TECH MODE — reducér visuel støj (Fix #15)
// ─────────────────────────────────────────────────────────
export function useCalmMode() {
  const [aktiv, setAktiv] = useState(() => localStorage.getItem('sv_calm_mode') === '1');
  useEffect(() => {
    if (aktiv) document.body.classList.add('sv-calm');
    else document.body.classList.remove('sv-calm');
  }, [aktiv]);
  const toggle = () => {
    setAktiv(p => {
      const ny = !p;
      localStorage.setItem('sv_calm_mode', ny ? '1' : '0');
      return ny;
    });
  };
  return { aktiv, toggle };
}
